"use client";

import { ArrowUp, Bot, PlugZap, Sparkles, WifiOff } from "lucide-react";
import { useEffect, useState } from "react";
import {
  getAgentStatus,
  getGroundedHelpDeskReply,
  type AgentStatus,
} from "@/src/services/aiAgentService";
import { useLang } from "@/src/i18n";

type Message = {
  id: string;
  role: "assistant" | "user";
  text: string;
  citation?: string;
  model?: string;
};

export function HelpDeskPanel() {
  const { t } = useLang();
  const [messages, setMessages] = useState<Message[]>(() => [
    { id: "seed-1", role: "assistant", text: t("help.seed1"), citation: "ECG 07:42" },
    { id: "seed-2", role: "assistant", text: t("help.seed2"), citation: "Chem7 08:00" },
  ]);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const [agent, setAgent] = useState<AgentStatus>({
    mode: "simulation",
    provider: "Checking connection",
    model: "Nong SATI",
  });

  useEffect(() => {
    void getAgentStatus().then(setAgent);
  }, []);

  async function send(value = input) {
    const trimmed = value.trim();
    if (!trimmed || thinking) return;
    setMessages((current) => [...current, { id: `${Date.now()}-u`, role: "user", text: trimmed }]);
    setInput("");
    setThinking(true);
    const reply = await getGroundedHelpDeskReply(trimmed);
    setAgent((current) => ({ ...current, mode: reply.mode, model: reply.model }));
    setMessages((current) => [
      ...current,
      { id: `${Date.now()}-a`, role: "assistant", text: reply.text, citation: reply.citation, model: reply.model },
    ]);
    setThinking(false);
  }

  return (
    <section className="panel help-panel">
      <div className="panel-heading help-heading">
        <div><Bot size={18} /><span>{t("help.title")}</span></div>
        <div className="help-agent-badges">
          <span className="persona"><Sparkles size={12} /> Nong SATI</span>
          <span
            className={`agent-state ${agent.mode}`}
            title={agent.mode === "live" ? `${agent.provider} · ${agent.model}` : "The real agent gateway is installed; this environment is using the safe demo fallback."}
          >
            {agent.mode === "live" ? <PlugZap size={11} /> : <WifiOff size={11} />}
            {agent.mode === "live" ? t("help.agentLive") : t("help.sim")}
          </span>
        </div>
      </div>
      <div className="chat-messages" aria-live="polite">
        {messages.map((message) => (
          <div className={`chat-bubble ${message.role}`} key={message.id}>
            <p>{message.text}</p>
            {message.citation && <span className="citation-chip">[{message.citation}]</span>}
            {message.model && <span className="message-model">{message.model}</span>}
          </div>
        ))}
        {thinking && <div className="chat-bubble assistant is-thinking">{t("help.thinking")}</div>}
      </div>
      <div className="suggested-questions">
        {[t("help.q1"), t("help.q2"), t("help.q3"), t("help.q4")].map((question) => (
          <button
            key={question}
            type="button"
            className="suggested-question"
            onClick={() => void send(question)}
            disabled={thinking}
          >
            {question}
          </button>
        ))}
      </div>
      <div className="chat-input">
        <input
          aria-label="Ask Nong SATI"
          value={input}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") void send();
          }}
          placeholder={t("help.placeholder")}
        />
        <button type="button" onClick={() => void send()} disabled={!input.trim() || thinking} aria-label="Send message">
          <ArrowUp size={16} />
        </button>
      </div>
    </section>
  );
}
